import { useTranslations } from '@vocab/react';
import {
  Textarea,
  Box,
  Inline,
  Button,
  Dialog,
  Text,
} from 'braid-design-system';
import type { ChangeEvent } from 'react';

import { CONTENT_CHARACTER_LIMIT } from 'src/core/domain/constants';
import type { Author } from 'src/mapper/threadMapper';

import ThreadPersonalDetails from '../ThreadPersonalDetails/ThreadPersonalDetails';

import translations from './.vocab';

import * as styles from './CommentTextArea.css';

interface CommentTextAreaProps {
  id: string;
  ariaLabel: string;
  value?: string;
  error: string | null;
  isOpen: boolean;
  author: Author;
  discardState: boolean;
  onChange?: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  handleSubmit?: () => void;
  onClose: () => void;
  handleDismissError: () => void;
  handleDiscard: () => void;
  handleDiscardCancel: () => void;
}

export default ({
  id,
  ariaLabel,
  value = '',
  author,
  error,
  discardState = false,
  onChange = () => {},
  handleSubmit = () => {},
  isOpen,
  onClose,
  handleDismissError,
  handleDiscard,
  handleDiscardCancel,
}: CommentTextAreaProps) => {
  const { t } = useTranslations(translations);
  return (
    <Box className={styles.mobileCommentText}>
      {discardState && (
        <Dialog
          id={`discard-comment-${id}`}
          title={t('discardComment')}
          description={<Text>{t('discardCommentDescription')}</Text>}
          open={isOpen}
          onClose={handleDiscard}
          width="small"
          closeLabel={t('discard')}
        >
          <Inline space="small" align="left">
            <Button variant="ghost" tone="critical" onClick={handleDiscard}>
              {t('discard')}
            </Button>
            <Button
              variant="transparent"
              tone="formAccent"
              onClick={handleDiscardCancel}
            >
              {t('cancel')}
            </Button>
          </Inline>
        </Dialog>
      )}
      {!discardState && (
        <Dialog
          id={`comment-${id}`}
          title=""
          open={isOpen}
          onClose={onClose}
          width="small"
          closeLabel="Cancel"
        >
          <ThreadPersonalDetails author={author} postingDate="Now" />
          <Textarea
            id={id}
            aria-label={ariaLabel}
            value={value}
            onChange={onChange}
            placeholder={t('initialMessage')}
            characterLimit={CONTENT_CHARACTER_LIMIT}
            tone={error ? 'critical' : 'neutral'}
            message={error ? error : ''}
          />
          <Box display="flex" justifyContent="spaceBetween" alignItems="center">
            <Inline space="small" align="left">
              {error ? (
                <Button
                  variant="ghost"
                  tone="critical"
                  onClick={handleDismissError}
                >
                  OK
                </Button>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    tone="formAccent"
                    onClick={handleSubmit}
                  >
                    {t('reply')}
                  </Button>
                  <Button
                    variant="transparent"
                    tone="formAccent"
                    onClick={onClose}
                  >
                    {t('cancel')}
                  </Button>
                </>
              )}
            </Inline>
            {value.length < CONTENT_CHARACTER_LIMIT - 150 && (
              <Text tone="secondary">
                {value.length}/{CONTENT_CHARACTER_LIMIT}
              </Text>
            )}
          </Box>
        </Dialog>
      )}
    </Box>
  );
};
